import React, {useEffect} from 'react';
import {Box, Button} from '@mui/material';
import {UPDATES} from '../../../shared/UPDATES';
import {BuilderPlanFormsStyled} from '../CreateBuilder/BuilderSourceDestination/BuilderPlanForm';
import {SetUpNewSubplan} from './SetUpNewSubplan';
import type {newPlanFields, newSubplanFields} from './builderSetUpNewFields';
import {Pot, Pot_children} from '../../../schema-types/Pot';
import {PlanProviderSelectOption} from '../../../hooks/usePlanNamesProviders';
import {
  useAutoField,
  useAutoForm,
  AutoFieldHelpers,
} from '../../../components/AutoForm/useAutoForm';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {isBondPlan} from './bondSetUpNew';
import {defaultFeeFields} from '../CreateBuilder/BuilderPlanFees';

export const getSavedChildSubPlans = (pots: Pot[], parentId: string): Pot_children[] => {
  if (!pots) {
    return [];
  }
  const parent = pots?.find((p) => p.id === parentId);
  if (!parent) {
    return [];
  }
  const {children} = parent;
  return children || [];
};

export const toggleSubPlanChecked = ({
  index,
  subplans,
  subplansHelpers,
}: {
  index: number;
  subplans: newSubplanFields[];
  subplansHelpers: AutoFieldHelpers<newPlanFields['subplans']>;
}) => {
  const filtered = subplans.filter((_, i) => i !== index);
  subplansHelpers.setValue(UPDATES._D460, filtered);
};

interface BuilderWrappedNewSubPlansProps {
  productProviders: PlanProviderSelectOption[] | undefined;
}

export const BuilderWrappedNewSubPlans: React.FC<BuilderWrappedNewSubPlansProps> = ({
  productProviders,
}) => {
  const {values} = useAutoForm<builderFieldsType>();
  const isBond = isBondPlan(values.mainPlanType);
  const [subplansFieldObj, , subplansHelpers] =
    useAutoField<newPlanFields['subplans']>('setUpNew.subplans');

  const subplans = subplansFieldObj.value ?? [];

  const changeSubPlan = (index: number) => {
    toggleSubPlanChecked({index, subplans, subplansHelpers});
  };

  const handleAddSubplan = () => {
    const newSubplan: newSubplanFields = {
      existingPotId: null,
      planFees: defaultFeeFields,
    };
    subplansHelpers.setValue(UPDATES._D530, [...subplans, newSubplan]);
  };

  const handleRemoveSubplan = (index: number) => {
    subplansHelpers.setValue(
      UPDATES._D531,
      subplans.filter((_, i) => i !== index)
    );
  };

  return (
    <Box ml={2}>
      <BuilderPlanFormsStyled>
        {subplans.map((subplan, index) => (
          <Box key={subplan.existingPotId ?? `new-${index}`}>
            <SetUpNewSubplan
              index={index}
              subplanId={subplan.existingPotId}
              formikKeyPrefix={`setUpNew.subplans.${index}`}
              handleChange={() => changeSubPlan(index)}
              productProviders={productProviders}
              onRemove={
                !isBond && subplan.existingPotId === null
                  ? () => handleRemoveSubplan(index)
                  : undefined
              }
            />
          </Box>
        ))}
      </BuilderPlanFormsStyled>
      {!isBond && (
        <Box mt={1} display="flex" justifyContent="center">
          <Button variant="contained" size="large" onClick={handleAddSubplan}>
            Add Subplan
          </Button>
        </Box>
      )}
    </Box>
  );
};
